import type { PatientPage as Page, PatientSummary } from "@allergy123/contracts/clinical";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useClinical } from "../api";
import { errorText } from "../lib/errors";
import { SEX_LABELS, formatDate, fullName } from "../lib/format";
import { useSession } from "../session";

/** Server-side search (api-v2.md Q-74): a name prefix or an exact date of birth; under two characters the list is the location's. */
export function PatientsPage() {
  const s = useSession();
  const api = useClinical();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState<{ items: PatientSummary[]; nextCursor: string | null; total: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locationId = s.location?.locationId;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const input = debounced.length >= 2 ? { query: debounced, limit: 20 } : locationId ? { locationId, limit: 20 } : null;
    if (!input) {
      setPage({ items: [], nextCursor: null, total: 0 });
      setLoading(false);
      return;
    }
    api.patients
      .search(input)
      .then((res: Page) => live && setPage(res))
      .catch((err: unknown) => live && setError(errorText(err)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [api, debounced, locationId]);

  async function more() {
    if (!page?.nextCursor) return;
    setLoading(true);
    try {
      const input = debounced.length >= 2 ? { query: debounced, limit: 20, cursor: page.nextCursor } : { locationId: locationId!, limit: 20, cursor: page.nextCursor };
      const res = await api.patients.search(input);
      setPage({ items: [...page.items, ...res.items], nextCursor: res.nextCursor, total: res.total });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Patients</h1>
        <p className="max-w-[68ch] text-ink-secondary">Search by name (last name, or “last, first”) or by date of birth as YYYY-MM-DD. Without a search, the list is this location's patients.</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <div className="mb-4">
            <Field label="Search" wide placeholder="e.g. Example or 1988-04-12" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
          </div>
          {error && <Status kind="error" label={`Couldn't search: ${error}`} className="mb-4 block" />}
          <Table caption={loading && !page ? "Searching…" : page ? `${page.total} patient${page.total === 1 ? "" : "s"}` : ""}>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Born</Th>
                <Th>Sex</Th>
                <Th>Last encounter</Th>
              </tr>
            </thead>
            <tbody>
              {page && page.items.length === 0 && !loading && (
                <tr>
                  <Td colSpan={4} className="text-ink-secondary">
                    {debounced.length >= 2 ? "No patient matches." : "No patients at this location yet."}
                  </Td>
                </tr>
              )}
              {page?.items.map((p) => (
                <tr key={p.patientId}>
                  <Td>
                    <Link to={`/patients/${p.patientId}`}>{fullName(p)}</Link>
                  </Td>
                  <Td>{p.dateOfBirth}</Td>
                  <Td>{SEX_LABELS[p.sex] ?? p.sex}</Td>
                  <Td>{p.lastEncounterAt ? formatDate(p.lastEncounterAt, s.location?.timezone ?? "UTC") : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          {page?.nextCursor && (
            <div className="mt-4">
              <Button variant="secondary" surface="card" disabled={loading} onClick={() => void more()}>
                Show more
              </Button>
            </div>
          )}
        </Card>
        {s.can("clinical:write") && <NewPatient onCreated={(id) => navigate(`/patients/${id}`)} />}
      </div>
    </>
  );
}

function NewPatient({ onCreated }: { onCreated: (patientId: string) => void }) {
  const s = useSession();
  const api = useClinical();
  const [form, setForm] = useState({ givenName: "", familyName: "", dateOfBirth: "", sex: "unknown", primaryLocationId: s.location?.locationId ?? "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const p = await api.patients.create({ givenName: form.givenName.trim(), familyName: form.familyName.trim(), dateOfBirth: form.dateOfBirth, sex: form.sex as "female" | "male" | "other" | "unknown", primaryLocationId: form.primaryLocationId });
      onCreated(p.patientId);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby="new-patient">
      <h2 id="new-patient" className="mb-4 text-h3 font-semibold text-anchor">
        New patient
      </h2>
      <form className="flex flex-col gap-4" onSubmit={(e) => void create(e)}>
        <Field label="First name" required value={form.givenName} onChange={set("givenName")} autoComplete="off" />
        <Field label="Last name" required value={form.familyName} onChange={set("familyName")} autoComplete="off" />
        <Field label="Date of birth" type="date" required value={form.dateOfBirth} onChange={set("dateOfBirth")} />
        <Select label="Sex" options={Object.entries(SEX_LABELS).map(([value, label]) => ({ value, label }))} value={form.sex} onChange={set("sex")} />
        <Select label="Primary location" options={s.locations.map((l) => ({ value: l.locationId, label: l.name }))} value={form.primaryLocationId} onChange={set("primaryLocationId")} required />
        {error && <Status kind="error" label={error} />}
        <div>
          <Button type="submit" disabled={busy || !form.primaryLocationId}>
            Create patient
          </Button>
        </div>
      </form>
    </Card>
  );
}
